/**
 * The facts a listing page states, in one place for both writers:
 *   - pages/buyer/VehicleDetail.js renders them for visitors, and
 *   - scripts/inject-seo-heads.js writes them into the static body, which is
 *     ALL an AI crawler sees — GPTBot, OAI-SearchBot, ClaudeBot and
 *     PerplexityBot do not run JavaScript.
 *
 * aeo/ render-audit (2026-09-29) flagged all 78 listings THIN_CONTENT: 33–47
 * words of raw HTML against a full spec table on the rendered page. Deriving
 * both from this file makes the crawler's copy the visitor's copy, never a
 * second version of it.
 *
 * Import-free on purpose: the injector loads it by stripping `export`.
 */

const inr = (n) => Number(n).toLocaleString('en-IN');

/** Where the car can be seen — the yard when BST holds it, else the seller's area. */
export function vehicleLocation(v) {
  if (v.location_type === 'bst_yard' && v.yard_name) {
    return `BST Yard: ${[v.yard_name, v.yard_address, v.yard_city].filter(Boolean).join(', ')}`;
  }
  return [v.state, v.city, v.landmark].filter(Boolean).join(' — ');
}

/** [label, value] rows for the spec table. Empty values are dropped. */
export function vehicleSpecRows(v) {
  return [
    ['Category', v.category],
    ['Brand', v.brand],
    ['Model', v.model],
    ['Year', v.year],
    ['KM Run', v.km_run ? `${inr(v.km_run)} km` : ''],
    ['Fuel Type', v.fuel_type],
    ['Transmission', v.transmission],
    ['Color', v.color],
  ].filter(([, value]) => value !== undefined && value !== null && String(value).trim() !== '');
}

/**
 * One self-contained, brand-attributed sentence — the passage an AI answer can
 * lift whole and still credit Buy Sell Taxi. Built only from listing fields;
 * it asserts nothing the listing does not.
 */
export function vehicleSummary(v) {
  const name = [v.year, v.brand, v.model].filter(Boolean).join(' ');
  const spec = [v.fuel_type, v.transmission && String(v.transmission).toLowerCase()].filter(Boolean).join(', ');
  const place = [v.city, v.state].filter(Boolean).join(', ');
  const seller = v.badge_type === 'bst_listing' ? 'as a BST Verified listing' : 'by a direct seller';
  const parts = [`This ${name}${spec ? ` (${spec})` : ''}`];
  if (v.km_run) parts.push(`has run ${inr(v.km_run)} km and`);
  const priced = v.price_final ? `₹${inr(v.price_final)}` : 'an undisclosed price';
  const verb = v.status === 'sold' ? 'was listed' : 'is listed';
  parts.push(`${verb} at ${priced}${place ? ` in ${place}` : ''} ${seller} on Buy Sell Taxi${v.status === 'sold' ? ' and has since been sold' : ''}.`);
  return parts.join(' ');
}
